import { useState } from "react";
import { CancelButton } from "../../elements/CancelButton";
import { useGlobalReducer } from "../../../GlobalContext";
import { SearchableSelect } from "../../blocks/fields/SearchableSelect";
import { Button } from "../../elements/Button";
import { Loading } from "../../elements/Loading";
import { Modal } from "../../elements/Modal";
import { PlusIcon } from "../../icons";
import { AddContactToMarketingList } from "../../api/marketingListService";
import {
  GetAllEntityDetailedInfo,
  GetRelatedEntityRecordsForContact,
} from "../../api/entityService";
import { isFailedResponse } from "../../api/request";
import {
  RelatedRecord,
  RelatedTableAddProps,
} from "../relatedTableActions.types";

interface MarketingList {
  Id: string;
  Name: string;
}

/**
 * Puts the contact on an existing marketing list. There can be hundreds of lists, so
 * they are picked from a searchable dropdown rather than shown as rows - the same
 * affordance the old contact form's Static Marketing Lists table has.
 */
export function AddMarketingListAction({
  parentId,
  records,
  onAdded,
}: RelatedTableAddProps) {
  const globalDispatch = useGlobalReducer();
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [availableLists, setAvailableLists] = useState<
    MarketingList[]
  >([]);
  const [selectedListId, setSelectedListId] = useState<string | null>(
    null
  );

  const openModal = async () => {
    setOpen(true);
    setLoading(true);

    // Only the lists the contact isn't on already.
    const existingListIds = new Set(
      records.map((record) => String(record.Fields.listid))
    );
    const response = await GetAllEntityDetailedInfo(
      globalDispatch,
      "list",
      false
    );

    if (!isFailedResponse(response)) {
      setAvailableLists(
        ((response.data ?? []) as MarketingList[]).filter(
          (list) => !existingListIds.has(String(list.Id))
        )
      );
    }

    setLoading(false);
  };

  const closeModal = () => {
    setOpen(false);
    setAvailableLists([]);
    setSelectedListId(null);
  };

  // Stays busy, with the modal open, until the contact is on the list and the table's
  // records have been read back - the new row's fields (member count and all) come
  // from the server, not from what was picked here.
  const handleAdd = async () => {
    if (!selectedListId) {
      return;
    }

    setIsSubmitting(true);

    const response = await AddContactToMarketingList(
      globalDispatch,
      selectedListId,
      parentId
    );

    if (!isFailedResponse(response)) {
      const related = await GetRelatedEntityRecordsForContact(
        globalDispatch,
        "listmember",
        parentId
      );

      if (!isFailedResponse(related)) {
        onAdded((related.data ?? []) as RelatedRecord[]);
      }

      closeModal();
    }

    setIsSubmitting(false);
  };

  const options = availableLists.map((list) => ({
    value: list.Id,
    label: list.Name,
  }));
  const busy = loading || isSubmitting;

  return (
    <>
      {/* The list page bar's secondary style, so it sits quietly beside the search
          rather than competing with the page's own actions. */}
      {/* No btn-* variant, so none competes with the classes below. */}
      <Button
        variant="none"
        className="border rounded-2 bg-white text-dark fw-medium hover-bg-light d-flex align-items-center"
        icon={PlusIcon}
        text="Add"
        onClick={openModal}
      />
      {open && (
        <Modal title="Add to marketing list" onClose={closeModal}>
          <div className="modal-body">
            {busy && <Loading />}
            {!busy && options.length === 0 && (
              <p className="text-center mb-0">
                There are no marketing lists available to add.
              </p>
            )}
            {!busy && options.length > 0 && (
              <SearchableSelect
                id="marketingList"
                placeholder="Select a marketing list"
                options={options}
                value={selectedListId}
                onChange={setSelectedListId}
                menuPosition="fixed"
              />
            )}
          </div>
          <div className="modal-footer d-block">
            <div className="w-100 d-flex justify-content-between">
              <CancelButton
                onClick={closeModal}
                disabled={isSubmitting}
              />
              <Button
                text="Add"
                variant="primary"
                onClick={handleAdd}
                showSpinner={isSubmitting}
                disabled={!selectedListId || isSubmitting}
              />
            </div>
          </div>
        </Modal>
      )}
    </>
  );
}

export default AddMarketingListAction;
